/**
 * A 3PL garment's small picture (BLU-1522 S6a), for the supply garment picker,
 * the per-garment match rows and the saved summary.
 *
 * The URL comes from the 3PL's own product or blank rows, so it can point at
 * an image that no longer loads. Every spot used to render the bare <img>,
 * which showed the browser's broken-image box. Now a missing or failing image
 * falls back to the same neutral shirt tile a garment with no image gets, and
 * a new URL gets a fresh try (the failure is remembered per URL, not forever).
 * The picture is decoration: the garment's title always sits beside it.
 */
import { useState } from 'react';
import { Shirt } from 'lucide-react';
import { cn } from '@/lib/utils';

const SIZE_CLASS = {
  sm: { tile: 'h-6 w-6 rounded', icon: 'h-3 w-3' },
  md: { tile: 'h-8 w-8 rounded-md', icon: 'h-3.5 w-3.5' },
} as const;

export function GarmentThumb({
  src,
  size = 'md',
  className,
}: {
  src: string | null | undefined;
  size?: keyof typeof SIZE_CLASS;
  /** Extra tile classes, e.g. a border. */
  className?: string;
}) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showImage = !!src && failedSrc !== src;
  const sizes = SIZE_CLASS[size];

  return (
    <span
      className={cn('grid shrink-0 place-items-center overflow-hidden bg-muted', sizes.tile, className)}
      data-state={showImage ? 'image' : 'placeholder'}
      aria-hidden="true"
    >
      {showImage ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
          onError={() => setFailedSrc(src)}
        />
      ) : (
        <Shirt className={cn(sizes.icon, 'text-muted-foreground')} aria-hidden="true" />
      )}
    </span>
  );
}
